import { DomainError } from '../errors/domain-error';
import { checkPermission } from '../rbac';
import { PermissionKey, UserRole } from '../enums';

/** D-EVT-011 access modes (Prisma + API source of truth). */
export const CreatorEventAccess = {
  PUBLIC: 'public',
  REGISTERED: 'registered',
  CIRCLE_MEMBERS: 'circle_members',
  CIRCLE_TIER: 'circle_tier',
  TICKET_HOLDERS: 'ticket_holders',
} as const;

export type CreatorEventAccessValue =
  (typeof CreatorEventAccess)[keyof typeof CreatorEventAccess];

export const CREATOR_EVENT_ACCESS_VALUES = Object.values(CreatorEventAccess);

export const CreatorEventType = {
  IN_PERSON: 'in_person',
  VIRTUAL: 'virtual',
  HYBRID: 'hybrid',
} as const;

export type CreatorEventTypeValue = (typeof CreatorEventType)[keyof typeof CreatorEventType];

export const CREATOR_EVENT_TYPE_VALUES = Object.values(CreatorEventType);

/** Labels for Studio selects. */
export const CREATOR_EVENT_ACCESS_LABELS: Record<CreatorEventAccessValue, string> = {
  public: 'Public (anyone with link)',
  registered: 'Signed-in members only',
  circle_members: 'Circle members',
  circle_tier: 'Circle tier gated',
  ticket_holders: 'Ticket holders',
};

export const CREATOR_EVENT_TYPE_LABELS: Record<CreatorEventTypeValue, string> = {
  in_person: 'In person',
  virtual: 'Virtual',
  hybrid: 'Hybrid',
};

/** Map legacy Functional Lite `circle_only` → `circle_members`. */
export function normalizeCreatorEventAccess(value: string | null | undefined): CreatorEventAccessValue {
  if (!value) return CreatorEventAccess.PUBLIC;
  if (value === 'circle_only') return CreatorEventAccess.CIRCLE_MEMBERS;
  if ((CREATOR_EVENT_ACCESS_VALUES as string[]).includes(value)) {
    return value as CreatorEventAccessValue;
  }
  throw new DomainError(`Unsupported event access mode: ${value}`);
}

export function isCreatorEventAccess(value: string): value is CreatorEventAccessValue {
  return (CREATOR_EVENT_ACCESS_VALUES as string[]).includes(value) || value === 'circle_only';
}

export function isCreatorEventType(value: string): value is CreatorEventTypeValue {
  return (CREATOR_EVENT_TYPE_VALUES as string[]).includes(value);
}

export function assertCreatorEventAccess(value: string): CreatorEventAccessValue {
  if (!isCreatorEventAccess(value)) {
    throw new DomainError('Invalid event access mode');
  }
  return normalizeCreatorEventAccess(value);
}

export function assertCreatorEventType(value: string): CreatorEventTypeValue {
  if (!isCreatorEventType(value)) {
    throw new DomainError('Invalid event type');
  }
  return value;
}

/** Studio/API mutations require CAN_MANAGE_EVENTS (S01). */
export function authorizeEventsManagement(role: UserRole): void {
  checkPermission(role, PermissionKey.CAN_MANAGE_EVENTS);
}

/**
 * Public discoverability of an event listing (not join entitlement).
 * Full entitlement evaluation lands in S05.
 */
export function isEventPubliclyListable(access: CreatorEventAccessValue | string): boolean {
  const normalized = normalizeCreatorEventAccess(access);
  return normalized === CreatorEventAccess.PUBLIC || normalized === CreatorEventAccess.REGISTERED;
}

/** Whether anonymous viewers may open the public page shell. */
export function allowsAnonymousEventPageView(access: CreatorEventAccessValue | string): boolean {
  const normalized = normalizeCreatorEventAccess(access);
  return (
    normalized === CreatorEventAccess.PUBLIC ||
    normalized === CreatorEventAccess.REGISTERED ||
    normalized === CreatorEventAccess.TICKET_HOLDERS
  );
}
